import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Gift } from 'lucide-react'
import type { DesktopAthlete } from '@/api/athletesApi'
import type { Group } from '@/types'
import { birthdayAgeLabel, upcomingBirthdays } from './birthdays'

interface Props {
  athletes: DesktopAthlete[]
  groups?: Group[]
  loading: boolean
  failed: boolean
  retry: () => void
}

export function BirthdaysPanel({ athletes, groups, loading, failed, retry }: Props) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const refresh = () => setNow(new Date())
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
    const timer = window.setTimeout(refresh, Math.max(1000, midnight.getTime() - Date.now() + 100))
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => { window.clearTimeout(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh) }
  }, [now])
  const birthdays = upcomingBirthdays(athletes, now)
  const today = birthdays.filter(row => row.daysAway === 0)
  const next = birthdays.filter(row => row.daysAway > 0)
  const missing = athletes.filter(athlete => !athlete.date_of_birth).length
  const rows = (items: typeof birthdays) => <ul className="divide-y" style={{ borderColor: 'var(--separator)' }}>{items.map(row => <li key={row.athlete.id}>
    <Link className="flex items-center gap-3 py-3" to={`/athletes?athlete=${encodeURIComponent(row.athlete.id)}`} aria-label={`Открыть карточку ${row.athlete.name}`}>
      <div className="rounded-lg shrink-0 min-w-[70px] px-2 py-2 text-center text-xs font-semibold" style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>{row.daysAway === 0 ? 'Сегодня' : new Date(`${row.date}T12:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}</div>
      <div className="min-w-0 flex-1"><div className="text-sm font-semibold break-words">{row.athlete.name}</div><div className="pc-muted text-xs mt-1">{row.athlete.group_id && !groups ? 'Группа не загружена' : groups?.find(group => group.id === row.athlete.group_id)?.name || 'Без группы'}{row.athlete.paused_at ? ' / На паузе' : ''}</div>{row.shiftedLeapDay && <div className="pc-muted text-xs mt-1">Дата рождения 29 февраля</div>}</div>
      <span className="text-xs whitespace-nowrap">{birthdayAgeLabel(row.age)}</span><ArrowUpRight size={14} className="shrink-0" />
    </Link>
  </li>)}</ul>
  return <section className="pc-panel" aria-label="Дни рождения спортсменов">
    <div className="flex items-center gap-2 mb-2"><Gift size={18} style={{ color: 'var(--accent)' }} /><h2 className="pc-section-title">Дни рождения</h2><Link to="/athletes" className="pc-link text-xs ml-auto">Все спортсмены</Link></div>
    <p className="pc-muted">Сегодня и ближайшие 30 дней в выбранном направлении. Указан возраст, который исполнится.</p>
    {failed ? <p className="pc-error mt-4" role="alert">Не удалось загрузить дни рождения. <button className="underline" onClick={retry}>Повторить</button></p>
      : loading ? <p className="pc-muted py-6" role="status">Загружаем дни рождения...</p>
        : <><div className="max-h-80 overflow-y-auto mt-4">
          {today.length > 0 && <div><h3 className="pc-eyebrow">Сегодня ({today.length})</h3>{rows(today)}</div>}
          {next.length > 0 && <div className={today.length ? 'mt-4' : ''}><h3 className="pc-eyebrow">Ближайшие ({next.length})</h3>{rows(next)}</div>}
          {!birthdays.length && <p className="pc-muted py-4">В ближайшие 30 дней дней рождения нет</p>}
        </div>{missing > 0 && <p className="pc-muted text-xs mt-3">Без даты рождения: {missing}. Её можно заполнить в карточке спортсмена.</p>}</>}
  </section>
}
